import { actionThrottling } from '@sato/components';

import { awsRum } from './awsRum.utils';

// Browser monitoring: error reporting and custom events, using the CloudWatch RUM client.
// The RUM client is initialized in `initBrowserMonitoring.ts`.

export enum MonitoringEvent {
  INCIDENT_INPUT_STARTED = 'incident-input-started',
}

const noticeError = (error: unknown) => {
  awsRum.recordError(error);
};

const sendEvent = (event: MonitoringEvent, payload?: object) => {
  awsRum.recordEvent(event, payload ?? {});
};

const actionNameForConditionalEvent = (conditionKey: string) =>
  `monitoring-conditional-event/${conditionKey}`;

/**
 * Sends the given monitoring event (with the given payload) if and only if an event
 * with the given `conditionValue` for the given `conditionKey` was not previously sent
 * during the current browser session (i.e. tab/window), within the given expiration
 * period.
 *
 * i.e.: if this is called >1 times with the same `condition{Key,Value}`, only one
 * event is sent (on the first invocation), until `expirationMs` has elapsed (after
 * which another event may be sent).
 */
const sendConditionalEvent = (args: {
  /**
   * Identifies the feature or circumstance we want to monitor (e.g. "user-is-logging-in")
   */
  conditionKey: string;

  /**
   * Value identifying a unique instance of the feature or circumstance we want to monitor
   * (e.g. "username of the user who is logging in")
   */
  conditionValue: string;

  /**
   * The expiration period for the persisted condition state, in milliseconds.
   */
  expirationMs?: number;

  event: MonitoringEvent;
  payload?: object;
}) => {
  actionThrottling.throttlePersistedAction({
    actionName: actionNameForConditionalEvent(args.conditionKey),
    parameter: args.conditionValue,
    expirationMs: args.expirationMs,
    action: () => {
      sendEvent(args.event, args.payload);
    },
  });
};

/**
 * Clears the persisted event condition state for the given key.
 * This means that on the next invocation of `sendConditionalEvent()`
 * for this key, the event would be sent.
 */
const resetEventConditionForKey = (conditionKey: string) => {
  actionThrottling.clearPersistedActionThrottling({
    actionName: actionNameForConditionalEvent(conditionKey),
  });
};

export const monitoringUtils = {
  sendEvent,
  sendConditionalEvent,
  resetEventConditionForKey,
  noticeError,
};
